// Artifacts library model — the shared, presentation-agnostic layer the ArtifactsPage (and the
// project detail's artifacts panel) build on. Type registry, retention math, and formatters live here
// so the table and card layouts can't drift on labels, colors, or expiry tiers.

import type { SvgIconComponent } from '@mui/icons-material'
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import type { SessionArtifact, PanelArtifactVersion } from '../components/rosArtifacts'

// Mirror of the backend policy constants (lib/artifact-limits.ts). Kept in sync by intent — the FE
// derives expiry; the BE clamps extends to MAX. A richer per-type policy is a deferred task.
export const DEFAULT_RETENTION_DAYS = 180
export const MAX_RETENTION_DAYS = 365

// ── Type registry ──────────────────────────────────────────────────────────
// Published artifact types Ros produces today. `tone` maps to a Studio-token color used for the
// type tile + chip; `ext` is the native download extension.
export interface ArtifactTypeDef {
  label: string
  tone: 'cyan' | 'info' | 'success' | 'ochre' | 'stone'
  ext: string
  icon: SvgIconComponent
}

export const ARTIFACT_TYPES: Record<string, ArtifactTypeDef> = {
  crosstabs: { label: 'Crosstab', tone: 'success', ext: '.csv', icon: PivotTableChartIcon },
  website: { label: 'Website', tone: 'cyan', ext: '.html', icon: CodeOutlinedIcon },
  survey: { label: 'Survey', tone: 'ochre', ext: '.md', icon: SubjectOutlinedIcon },
  markdown: { label: 'Markdown', tone: 'info', ext: '.md', icon: DescriptionOutlinedIcon },
  'app-artifact': { label: 'App Artifact', tone: 'cyan', ext: '.zip', icon: AppsOutlinedIcon },
}

export function typeDef(type: string): ArtifactTypeDef {
  return ARTIFACT_TYPES[type] ?? { label: type.charAt(0).toUpperCase() + type.slice(1), tone: 'stone', ext: '.txt', icon: InsertDriveFileOutlinedIcon }
}

// Studio-token hex per tone (filled tile bg uses the tint; chip uses solid text on faint tint).
export const TONE_COLOR: Record<ArtifactTypeDef['tone'], string> = {
  cyan: '#008FA8',
  info: '#3A6E8F',
  success: '#2E8B5C',
  ochre: '#B27A28',
  stone: '#57524a',
}

// ── Retention ────────────────────────────────────────────────────────────────
export type RetentionTier = 'ok' | 'soon' | 'urgent' | 'expired'

export interface RetentionState {
  retentionDays: number
  expiresAt: number          // ms epoch
  daysLeft: number
  tier: RetentionTier
  label: string              // "Expires in 4 days" / "Expires Aug 10" / "Expired"
  atMax: boolean
}

const DAY_MS = 86_400_000

/** Derive a version's expiry from its publishedAt (its created date) + retention_days. */
export function retentionFor(v: PanelArtifactVersion, now = Date.now()): RetentionState {
  const retentionDays = v.retentionDays ?? DEFAULT_RETENTION_DAYS
  const created = Date.parse(v.publishedAt)
  const expiresAt = created + retentionDays * DAY_MS
  const daysLeft = Math.round((expiresAt - now) / DAY_MS)
  const tier: RetentionTier = daysLeft <= 0 ? 'expired' : daysLeft <= 3 ? 'urgent' : daysLeft <= 14 ? 'soon' : 'ok'
  const label =
    daysLeft <= 0 ? 'Expired'
    : daysLeft === 1 ? 'Expires tomorrow'
    : daysLeft <= 14 ? `Expires in ${daysLeft} days`
    : `Expires ${new Date(expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
  return { retentionDays, expiresAt, daysLeft, tier, label, atMax: retentionDays >= MAX_RETENTION_DAYS }
}

export const TIER_COLOR: Record<RetentionTier, string> = {
  ok: '#57524a',       // stone — calm
  soon: '#B27A28',     // ochre — heads-up
  urgent: '#C7442E',   // terracotta-danger — act soon
  expired: '#C7442E',
}

// ── Formatters ─────────────────────────────────────────────────────────────
export function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function relativeModified(publishedAt: string, now = Date.now()): string {
  const days = Math.floor((now - Date.parse(publishedAt)) / DAY_MS)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return 'Last week'
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  return new Date(publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Fetch + shape ────────────────────────────────────────────────────────────
/**
 * Flatten the grouped /api/r3/artifacts response into one artifact list (newest version first each).
 * The backend withholds the `markdown` group unless its trusted feature snapshot enables it. Callers
 * also pass `includeMarkdown` from that snapshot so cached or mocked responses cannot expose the new
 * artifact type while the rollout flag is off.
 */
export function flattenArtifacts(
  groups: Array<{ type: string; items: SessionArtifact[] }>,
  includeMarkdown = false,
  includeAppArtifacts = false,
): SessionArtifact[] {
  return groups
    .filter(g => (includeMarkdown || g.type !== 'markdown')
      && (includeAppArtifacts || g.type !== 'app-artifact'))
    .flatMap(g => g.items)
}

export const ART_SORTS = [
  { id: 'modified', label: 'Recently modified' },
  { id: 'expiring', label: 'Expiring soon' },
  { id: 'name', label: 'Name' },
] as const
export type ArtSortId = (typeof ART_SORTS)[number]['id']

/** Most-recent version of an artifact (drives the parent row's modified/retention). */
export function latestVersion(a: SessionArtifact): PanelArtifactVersion {
  return a.versions.reduce((newest, v) => (v.version > newest.version ? v : newest), a.versions[0])
}
